/**
Copyright [2022] [https://www.merac.com
 */
import { createRouter, createWebHistory } from 'vue-router'
import { notification } from 'ant-design-vue'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'
import systemRouter from './systemRouter'
import { afterEach, beforeEach } from './scrollBehavior'
import whiteListRouters from './whiteList'
import userCenterApi from '@/api/sys/userCenterApi'
import loginApi from '@/api/auth/loginApi'
import userRoutes from '@/config/route'
import { isMenuCacheValid, saveMenuCache } from '@/utils/menuCache'
import { normalizeMenuTree } from '@/utils/menu'
import tool from '@/utils/tool'
import { cloneDeep } from 'lodash-es'
import { isPcBusinessRoute, isPcRouteEnabled, resolvePcRouteCapabilityCodes } from '@/constants/pcAppRegistry.mjs'
import { loadBmobileAppCatalog, loadPcAppCatalog } from '@/views/biz/staff-domain/shared'
import { hasPerm } from '@/utils/permission'
const modules = import.meta.glob('/src/views/**/**.vue')
import { globalStore, searchStore } from '@/store'

// 进度条配置
NProgress.configure({ showSpinner: false, speed: 500 })

// 系统特殊路由
const routes_404 = [
	{
		path: '/:pathMatch(.*)*',
		hidden: true,
		component: () => import('@/layout/other/404.vue')
	}
]
// 系统路由
const routes = [...systemRouter, ...whiteListRouters, ...routes_404]

const router = createRouter({
	history: createWebHistory(),
	routes
})

// 设置标题
// document.title = sysBaseConfig.DIGIT_SYS_NAME

// 判断是否已加载过动态/静态路由
const isGetRouter = ref(false)
let loginProfileToken = ''

// 白名单校验
const exportWhiteListFromRouter = (router) => {
	const res = []
	for (const item of router) res.push(item.path)
	return res
}
const whiteList = exportWhiteListFromRouter(whiteListRouters)

router.beforeEach(async (to, from, next) => {
	NProgress.start()
	const store = globalStore()

	const sysBaseConfig = tool.data.get('DIGIT_SYS_BASE_CONFIG') || store.sysBaseConfig
	// 动态标题
	document.title = to.meta.title
		? `${to.meta.title} - ${sysBaseConfig.DIGIT_SYS_NAME}`
		: `${sysBaseConfig.DIGIT_SYS_NAME}`

	// 过滤白名单
	if (whiteList.includes(to.path)) {
		next()
		// NProgress.done()
		return false
	}

	const token = tool.data.get('TOKEN')
	if (to.path === '/login') {
		// 已登录用户进入后台，不跳转到公开站点首页
		if (token) {
			next({
				path: '/admin'
			})
			return false
		}
		// 删除路由(替换当前layout路由)
		router.addRoute(routes[0])
		isGetRouter.value = false
		next()
		return false
	} else {
		// 这里需要使用 localStorage 保存登录之前要访问的页面
		tool.data.set('LAST_VIEWS_PATH', to.fullPath)
	}
	if (!token) {
		next({
			path: '/login'
		})
		return false
	}
	if (loginProfileToken !== token) {
		try {
			const loginUser = await loginApi.getLoginUser()
			loginUser.capabilityCodes = Array.isArray(loginUser.capabilityCodes) ? loginUser.capabilityCodes : []
			store.setUserInfo(loginUser)
			tool.data.set('USER_INFO', loginUser)
			loginProfileToken = token
		} catch {
			// Profile refresh is best effort; keep the cached identity if it fails.
		}
	}
	// 整页路由处理
	if (to.meta.fullpage) {
		to.matched = [to.matched[to.matched.length - 1]]
	}
	const managedRoute = resolvePcRouteCapabilityCodes(to.path)
	if (managedRoute === null && isPcBusinessRoute(to.path)) {
		notification.error({
			message: '路由未登记',
			description: '当前业务页面未声明能力编码，已拒绝访问。'
		})
		next({ path: '/index', replace: true })
		return false
	}
	if (managedRoute?.length) {
		if (!hasPerm(managedRoute)) {
			notification.warning({
				message: '暂无权限',
				description: '当前员工未授权该功能，已返回应用桌面。'
			})
			next({ path: '/index', replace: true })
			return false
		}
		try {
			const [catalog, pcCatalog] = await Promise.all([loadBmobileAppCatalog(), loadPcAppCatalog()])
			if (!isPcRouteEnabled(to.path, catalog, pcCatalog)) {
				notification.warning({
					message: '应用暂未启用',
					description: '当前功能已从应用目录停用，已返回应用桌面。'
				})
				next({ path: '/index', replace: true })
				return false
			}
		} catch (error) {
			notification.error({
				message: '应用目录不可用',
				description: '暂时无法确认当前应用状态，请稍后重试。'
			})
			next({ path: '/index', replace: true })
			return false
		}
	}
	// 加载动态/静态路由
	if (!isGetRouter.value) {
		let apiMenu = tool.data.get('MENU') || []

		if (token && !isMenuCacheValid(apiMenu)) {
			apiMenu = normalizeMenuTree(await userCenterApi.userLoginMenu())
			saveMenuCache(apiMenu)
		}

		if (apiMenu.length === 0) {
			// 创建默认模块，显示默认菜单
			apiMenu[0] = cloneDeep(userRoutes.module[0])
			const userMenu = userRoutes.menu
			const childrenApiMenu = apiMenu[0].children
			apiMenu[0].children = [...userMenu, ...childrenApiMenu]
		}

		let menuRouter = filterAsyncRouter(apiMenu)
		menuRouter = flatAsyncRoutes(menuRouter)

		menuRouter.forEach((item) => {
			router.addRoute('layout', item)
		})

		const search_store = searchStore()
		search_store.init(menuRouter)
		isGetRouter.value = true
		next({ ...to, replace: true })
		return false
	}
	beforeEach(to, from)
	next()
})

router.afterEach((to, from) => {
	afterEach(to, from)
	NProgress.done()
})

router.onError((error) => {
	NProgress.done()
	notification.error({
		message: '路由错误',
		description: error.message
	})
})

// 入侵追加自定义方法、对象
router.getMenu = () => {
	let apiMenu = tool.data.get('MENU') || []
	// 增加固定路由
	if (apiMenu.length === 0) {
		// 创建默认模块，显示默认菜单
		apiMenu[0] = cloneDeep(userRoutes.module[0])
		const userMenu = userRoutes.menu
		const childrenApiMenu = apiMenu[0].children
		apiMenu[0].children = [...userMenu, ...childrenApiMenu]
	}
	return apiMenu
}

// 转换
const filterAsyncRouter = (routerMap) => {
	const accessedRouters = []
	routerMap.forEach((item) => {
		item.meta = item.meta ? item.meta : {}
		// 处理外部链接特殊路由
		if (item.meta.type === 'iframe') {
			item.meta.url = item.path
			item.path = `/i/${item.name}`
		}

		// 添加可见性标记到meta，并保留顶级visible属性
		if (item.visible !== undefined) {
			// 同时设置meta.visible和保留顶级visible
			item.meta.visible = item.visible
		}

		// MAP转路由对象
		const route = {
			path: item.path,
			name: item.name,
			meta: item.meta,
			redirect: item.redirect,
			visible: item.visible, // 保留顶级visible属性
			children: item.children ? filterAsyncRouter(item.children) : null,
			component: loadComponent(item.component)
		}
		accessedRouters.push(route)
	})
	return accessedRouters
}
const loadComponent = (component) => {
	if (component) {
		if (component.includes('/')) {
			return modules[`/src/views/${component}.vue`]
		}
		return modules[`/src/views/${component}/index.vue`]
	} else {
		return modules[`/src/layout/other/empty.vue`]
	}
}

// 路由扁平化
const flatAsyncRoutes = (routes, breadcrumb = []) => {
	const res = []
	routes.forEach((route) => {
		const tmp = { ...route }
		if (tmp.children) {
			const childrenBreadcrumb = [...breadcrumb]
			childrenBreadcrumb.push(route)
			const tmpRoute = { ...route }
			tmpRoute.meta.breadcrumb = childrenBreadcrumb
			delete tmpRoute.children
			res.push(tmpRoute)
			const childrenRoutes = flatAsyncRoutes(tmp.children, childrenBreadcrumb)
			childrenRoutes.map((item) => {
				res.push(item)
			})
		} else {
			const tmpBreadcrumb = [...breadcrumb]
			tmpBreadcrumb.push(tmp)
			tmp.meta.breadcrumb = tmpBreadcrumb
			res.push(tmp)
		}
	})
	return res
}

export default router
